<script>
  import {Event} from '../../plugins/bus';
  import {TaskType} from '../../models/enums';
  import {GarageLocationStore} from '../../stores/GarageLocationStore';

  export default {
    data() {
      return {
        taskSortOrder: [TaskType.PreventativeMaintenance, TaskType.MajorService],
      };
    },
    computed: {
      querystring() {
        if (!this.params) {
          return '';
        }
        return '?' + Object.keys(this.params).map((key) => key + '=' + this.params[key]).join('&');
      },
      params() {
        return {
          from: this.from,
          to: this.to,
          garageLocationId: GarageLocationStore.data.userGarage,
        };
      },
    },
    methods: {
      async getService(serviceId) {
        try {
          this.$wait.start('loading service');
          this.$wait.start('loading service data');
          this.$wait.start('loading data');
          const response = await this.$api.get(`services/${serviceId}`);
          return response.data;
        } catch (error) {
          this.$toast.error('There was an error getting the service');
          return [];
        } finally {
          this.$wait.end('loading service');
          this.$wait.end('loading service data');
          this.$wait.end('loading data');
        }
      },
      async getServices() {
        try {
          this.$wait.start('loading services');
          const response = await this.$api.get(`services${this.querystring}`);
          return this.sortServicesAndTasks(response.data);
        } catch (error) {
          this.$toast.error('There was an error getting services', error);
          return [];
        } finally {
          this.$wait.end('loading services');
        }
      },
      async getUnitServices(unitId, query) {
        try {
          this.$wait.start('loading services');
          const response = await this.$api.get(`units/${unitId}/services${query ? query : this.querystring}`);
          return response.data;
        } catch (error) {
          this.$toast.error('There was an error getting services', error);
          return [];
        } finally {
          this.$wait.end('loading services');
        }
      },
      async completeService(serviceId, command) {
        try {
          this.$wait.start('completing service');
          await this.$api.post(`services/${serviceId}/complete`, command);
          this.$bus.$emit(Event.ServiceCompleted);
        } catch (error) {
          if (error.response.status === 403) {
            this.$toast.error(error.response.data);
          } else {
            this.$toast.error('There was an error completing the service');
          }
        } finally {
          this.$wait.end('completing service');
        }
      },
      confirmUpdateCompletedService(serviceId, command, attachments) {
        this.$confirmCancel.show(
          `Update Completed Service Tasks?`,
          `<p>Any modified service tasks will not be moved until the next time that projections are calculated</p>`,
          'Update tasks');

        const confirm = confirmed => {
          this.$confirmCancel.$off('confirm-cancel', confirm);
          if (!confirmed) {
            return;
          }
          this.updateCompletedService(serviceId, command)
            .then(data => {
              this.submitServiceTaskDocuments(serviceId, {...attachments}).then(_ => {
                this.$bus.$emit(Event.ServiceCompleted);
                this.$bus.$emit(Event.DocumentsUpdated);
              });
            })
            ;
        };

        this.$confirmCancel.$on('confirm-cancel', confirm);
      },
      async updateCompletedService(serviceId, command) {
        try {
          this.$wait.start('updating completed service');
          this.$wait.start('loading service data');
          await this.$api.post(`services/${serviceId}/update-tasks`, command);
        } catch (error) {
          if (error.response.status === 403) {
            this.$toast.error(error.response.data);
          } else {
            this.$toast.error('There was an error updating tasks for the service');
          }
        } finally {
          this.$wait.end('updating completed service');
          this.$wait.end('loading service data');
        }
      },
      async moveService(service, date) {
        try {
          this.$wait.start('moving service');
          this.$wait.start('loading service data');
          await this.$api.post(`services/${service.id}/move`, {date});
          this.$bus.$emit(Event.ServiceMoved, {unit: service.unitNumber, date});
        } catch (error) {
          this.$toast.error('There was an error moving the service');
        } finally {
          this.$wait.end('moving service');
          this.$wait.end('loading service data');
        }
      },
      async deleteCompletedService(serviceId) {
        try {
          const response = await this.$api.delete(`services/${serviceId}`);
          this.$bus.$emit(Event.ServiceDeleted, response.data);
        } catch (err) {
          this.$toast.error(`Could not delete service`);
        } finally {
          this.$toast.success(`Service deleted`);
        }
      },
      compareServices(a, b) {
        return a.unitNumber.localeCompare(b.unitNumber);
      },
      compareTasks(a, b) {
        const aIndex = this.taskSortOrder.indexOf(a.type);
        const bIndex = this.taskSortOrder.indexOf(b.type);

        if (aIndex === -1) {
          return 1;
        }
        if (bIndex === -1) {
          return -1;
        }
        return aIndex - bIndex;
      },
      sortServicesAndTasks(data) {
        data = data.sort(this.compareServices);
        data.forEach((s) => {
          s.tasks = s.tasks.sort(this.compareTasks);
        });

        return data;
      },
      async submitServiceTaskDocuments(serviceId, attachments) {
        for (const serviceTaskId of Object.keys(attachments)) {
          const files = attachments[serviceTaskId];
          if (files.length == 0) {
            await this.clearAllServiceDocuments(serviceId, serviceTaskId);
          } else {
            await this.uploadServiceTaskDocuments(serviceId, serviceTaskId, files);
          }
        }
      },
      async uploadServiceTaskDocuments(serviceId, serviceTaskId, files) {
        try {
          this.$wait.start('uploading service documents');
          const formData = new FormData();
          files.forEach((file) => {
            formData.append('files', file);
          });
          await this.$api.post(`services/${serviceId}/tasks/${serviceTaskId}/documents`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (error) {
          this.$toast.error('There was an error uploading the service documents');
        } finally {
          this.$wait.end('uploading service documents');
        }
      },
      async clearAllServiceDocuments(serviceId, serviceTaskId) {
        try {
          this.$wait.start('clearing service documents');
          await this.$api.delete(`services/${serviceId}/tasks/${serviceTaskId}/documents`);
        } catch (error) {
          this.$toast.error('There was an error clearing the service documents');
        } finally {
          this.$wait.end('clearing service documents');
        }
      }
    },
  };
</script>
